/**
 * Google Analytics Integration
 *
 * Provides event tracking across all Google Analytics tracker types:
 * legacy ga.js, Universal Analytics (analytics.js), and GA4 (gtag.js).
 *
 * Supports GA4 custom event parameters via the `data` argument on trackGAEvent.
 *
 * Also provides:
 * - registerClickListeners: data-ga-event and data-ganame click handlers
 * - initGoogleAnalytics: bootstraps Slzr.trackGAEvent and Slzr.GoogleAnalytics
 *   queue subscriber object
 *
 * Depends on Slzr.AnalyticsQueue (from analytics-queue.js) being initialized
 * before initGoogleAnalytics is called. The load order and DOM-ready
 * initialization are managed by init.js.
 */

/**
 * Send a command to all legacy ga.js trackers
 * @param {string} category - Event category
 * @param {string} action - Event action
 * @param {string} label - Event label
 * @param {number|undefined} value - Optional event value
 */
const sendToAllGAJS = (category, action, label, value = undefined) => {
  if (window._gaq && window._gat) {
    try {
      const trackers = window._gat._getTrackers();
      for (const tracker of trackers) {
        let prefix = tracker._getName();
        if (prefix !== "") {
          prefix += ".";
        }

        try {
          window._gaq.push([`${prefix}trackEvent`, category, action, label, value]);
        } catch (err) {
          console?.error(
            `Error tracking event (${prefix}, legacy)`,
            category,
            action,
            label,
            value,
            err,
          );
        }
      }
    } catch (err) {
      console?.error("Error getting legacy trackers", err);
    }
  }

  return true;
};

/**
 * Send a command to all active analytics.js universal analytics trackers
 * @param {string} command - GA command to send
 * @param {...any} rest - Additional arguments for the command
 */
const sendToAllAnalyticsJS = (command, ...rest) => {
  if (window.ga) {
    const {ga} = window;

    ga(() => {
      try {
        const trackers = ga.getAll();
        for (const tracker of trackers) {
          let prefix = tracker.get("name");
          if (prefix === "olark") continue;
          if (prefix === "t0") prefix = "";
          if (prefix !== "") prefix += ".";

          try {
            ga(`${prefix}${command}`, ...rest);
          } catch (err) {
            console?.error(
              `Error sending ga command (${prefix})`,
              command,
              ...rest,
            );
          }
        }
      } catch (err) {
        console?.error("Error getting analytics.js trackers", err);
      }
    });
  }

  return true;
};

/**
 * This relies on gtag's built in behavior of sending events to all configured trackers
 * @param {string} command - gtag command (usually 'event')
 * @param {string} category - Event category
 * @param {string} action - Event action/name
 * @param {string} label - Event label
 * @param {number|undefined} value - Optional event value
 * @param {Object|undefined} data - Optional custom event parameters
 */
const sendToAllGtags = (command, category, action, label, value = undefined, data = undefined) => {
  if (window.gtag) {
    try {
      const eventParams = {
        event_category: category,
        event_label: label,
        value: value,
        ...data,
      };
      window.gtag(command, action, eventParams);
    } catch (err) {
      console?.error("Error sending gtag command", command, action, {
        category,
        label,
        value,
        data,
      });
    }
  }

  return true;
};

/**
 * Track an event in all google analytics trackers
 *
 * @param {string} category - Event category (e.g., 'Admin Event')
 * @param {string} action - Event action/name (e.g., 'Generate AI Event Description')
 * @param {string} [label] - Event label (e.g., event name)
 * @param {number} [value] - Optional numeric value
 * @param {Object} [data] - Optional custom event parameters for GA4
 *
 * @example
 * // Basic event tracking
 * trackGAEvent('Admin Event', 'Open AI Event Description Generator', 'My Event Name');
 *
 * @example
 * // Event tracking with custom parameters
 * trackGAEvent('Admin Event', 'Generate AI Event Description', 'My Event Name', undefined, {
 *   ai_tone: 'Professional',
 *   ai_length: 'Standard',
 *   ai_includes_details: true,
 *   ai_refine_existing_description: ''
 * });
 */
export const trackGAEvent = (
  category,
  action,
  label,
  value = undefined,
  data = undefined,
) => {
  // GA4 gtags - custom data is included as event parameters and gtag
  // automatically pushes to dataLayer
  sendToAllGtags("event", category, action, label, value, data);
  // Universal analytics trackers
  sendToAllAnalyticsJS("send", "event", category, action, label, value);
  // Legacy ga.js trackers
  sendToAllGAJS(category, action, label, value);

  // Development mode logging when no GA is configured
  if (!window.gtag && !window.ga && !window._gaq) {
    console?.debug("[DEV GA] Would record analytic event: ", {
      category,
      action,
      label,
      value,
      data,
    });
  }

  return true;
};

/**
 * Register event listeners for automatic google analytics event tracking
 * on both data-ganame and data-ga-event attributes.
 */
export const registerClickListeners = () => {
  $(document).on('click', '[data-ga-event]', function() {
    const $this = $(this);

    const category = $this.data('ga-category') || $this.parents('[id!=""]:first').get(0)?.id;
    const action = $this.data('ga-action') || 'clicked';
    const label = $this.data('ga-label');

    trackGAEvent(category, action, label);
  });

  $(document).on('click', '[data-ganame]', function() {
    const el = $(this);

    const eventCategory = el.data('ganame');
    const eventLabel = el.data('ganame-label') || el.text() || el.val() || el.children('img:first').attr('alt');

    trackGAEvent(eventCategory, 'clicked', eventLabel);
  });
};

/**
 * Initialize the Google Analytics module
 *
 * Sets up:
 * - Slzr.trackGAEvent for use from non-module code
 * - Slzr.GoogleAnalytics with queue subscription (matching the Vitally pattern)
 *
 * Requires Slzr.AnalyticsQueue to be initialized before this is called.
 * Calls Slzr.GoogleAnalytics.init() after setting up the object.
 *
 * @param {Object} Slzr - The Slzr global object
 */
export const initGoogleAnalytics = (Slzr) => {
  Slzr.trackGAEvent = trackGAEvent;

  Slzr.GoogleAnalytics = {
    initialized: false,

    init() {
      if (this.initialized) return;

      this.initialized = true;
      this.subscribeToQueue();
    },

    subscribeToQueue() {
      if (!Slzr.AnalyticsQueue?.subscribe) return;

      Slzr.AnalyticsQueue.subscribe((event) => {
        if (
          event.providers?.length &&
          event.providers.includes(Slzr.AnalyticsQueue._providers.google)
        ) {
          trackGAEvent(event.category, event.action, event.label, event.value, event.data);
        }
      });
    }
  };
  Slzr.GoogleAnalytics.init();
};
